import React, { Children } from 'react';
import PropTypes from 'prop-types';
import CheckboxGroup from '../checkbox-group';
import { Map, List, is } from 'immutable';

class CheckboxParentGroup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      childValues: Map(),
      valuesChecked: List(),
    };
  }

  componentWillMount() {
    this.buildState(this.props.children, this.props.valuesChecked);
  }

  componentWillReceiveProps(nextProps) {
    const nextValuesChecked = List(nextProps.valuesChecked);
    // Rebuild the state only if it is necessary
    if (!is(nextValuesChecked, this.state.valuesChecked)) {
      this.buildState(nextProps.children, nextValuesChecked);
    }
  }

  // Given values from props, create the checked value state.
  buildState(children, valuesChecked) {
    let childValues = Map(this.buildChildrenMap(children, valuesChecked));
    childValues = this.setCheckedState(childValues, valuesChecked, null, true);
    this.setState({
      childValues,
      valuesChecked: List(valuesChecked),
    });
  }

  // Find the checked or partially-checked status of all immediate children.
  getChildStatus(node) {
    let status = Map({ all: false, some: false });
    if (!node.get('children')) {
      return status;
    }
    const checkedCount = node.get('children').filter(c => c.get('checked')).size;
    status = status.set('all', checkedCount === node.get('children').size);
    status = status.set('hasPartial', node.get('children').count(c => c.get('partial')) > 0);
    if (!status.get('all')) {
      status = status.set('some', checkedCount > 0);
    }
    return status;
  }

  // Take the existing child values and determine the next state.
  setCheckedState(childValues, valuesChecked, parent, isInitial = false) {
    return childValues.map((childNode, key) => {
      let nodeItem = childNode;
      if (nodeItem.get('children')) {
        nodeItem = nodeItem.set(
          'children',
          this.setCheckedState(nodeItem.get('children'), valuesChecked, nodeItem, isInitial)
        );
      }
      return nodeItem.withMutations(node => {
        const isChecked = valuesChecked.indexOf(key) >= 0;
        const isDisabled = node.get('disabled');

        if (!isDisabled && ((isInitial && isChecked) || node.get('checked') || (parent && parent.get('checked')))) {
          // This item is checked in the default values, in the current state,
          // or its parent is checked - mark it as checked.
          node.set('checked', true).set('partial', false);
        } else if (!isDisabled) {
          const childStatus = this.getChildStatus(node);
          if (childStatus.get('all')) {
            // All child items are checked - mark this parent as checked.
            node.set('checked', true).set('partial', false);
          } else if (childStatus.get('some') || childStatus.get('hasPartial', 0)) {
            // Some child items are checked - mark this parent as partial.
            node.set('checked', false).set('partial', true);
          } else {
            // No child items are checked or partially checked - mark this as unchecked.
            node.set('checked', false).set('partial', false);
          }
        }
        return node;
      });
    });
  }

  // Setup the initial state map from the list of children objects.
  buildChildrenMap(children, valuesChecked, isDisabled = false) {
    const map = {};
    Children.forEach(children, child => {
      const checked = valuesChecked.indexOf(child.props.value) >= 0;
      map[child.props.value] = Map({
        checked,
        disabled: child.props.disabled || isDisabled,
        children: child.props.children
          ? Map(this.buildChildrenMap(child.props.children, valuesChecked, child.props.disabled || isDisabled))
          : null,
      });
    });
    return map;
  }

  // Set the checked values of all child nodes based on the new checked value.
  setChildren(hierarchy, map, checkedValue) {
    const deepDive = map.getIn(hierarchy);
    if (!deepDive) {
      return map;
    }

    const keys = deepDive.keys();
    for (const key of keys) {
      const sub = hierarchy.push(key);

      const subChecked = sub.push('checked');

      const subDisabled = sub.push('disabled');
      const isDisabled = !!map.getIn(subDisabled);
      if (!isDisabled) {
        map.setIn(subChecked, checkedValue);
      }

      const subChildren = sub.push('children');
      this.setChildren(subChildren, map, checkedValue);
    }

    return map;
  }

  // Use the hierarchy of the changed item to determine the new state.
  getNewChildValues(hierarchy) {
    const hierarchyToChild = hierarchy.reduce((accum, value) => accum.push(value).push('children'), List()).pop();

    const hierarchyToChecked = hierarchyToChild.push('checked');
    const hierarchyToDisabled = hierarchyToChild.push('disabled');
    const hierarchyToChildren = hierarchyToChild.push('children');

    const newChildValues = this.state.childValues.withMutations(map => {
      const checkedValue = map.getIn(hierarchyToChecked);
      const disabledValue = map.getIn(hierarchyToDisabled);
      if (!disabledValue) {
        const newCheckedValue = !checkedValue;
        this.setChildren(hierarchyToChildren, map, newCheckedValue).setIn(hierarchyToChecked, newCheckedValue);

        // If this item is now deselected, any parents are now either deselected or partially selected.
        if (!newCheckedValue) {
          const uncheckHierarchy = hierarchy.pop();
          uncheckHierarchy.map(value => {
            const hierarchyToValue = hierarchyToChecked.slice(0, hierarchyToChecked.indexOf(value) + 1);
            if (!newCheckedValue) {
              map.setIn(hierarchyToValue.push('checked'), newCheckedValue);
            }
          });
        }
      }
    });
    return this.setCheckedState(newChildValues, this.props.valuesChecked);
  }

  // OnChange, grab the hierarchy of the changed item and rebuild state.
  onChangeCheckedItems(...hierarchyArr) {
    const hierarchy = List(hierarchyArr);
    const changedValue = hierarchy.last();
    const childValues = this.getNewChildValues(hierarchy);
    const valuesChecked = this.getValuesChecked(childValues);
    this.setState(
      {
        childValues,
        valuesChecked: List(valuesChecked),
      },
      () => {
        this.onChange(changedValue, valuesChecked);
      }
    );
  }

  // Get the array of selected values.
  getValuesChecked(childValues, list) {
    let values = list || [];
    const entries = childValues.entries();
    for (const [key, value] of entries) {
      const checked = value.get('checked');
      if (checked) {
        values.push(key);
      }
      const children = value.get('children');
      if (children) {
        values = this.getValuesChecked(children, values);
      }
    }

    return values;
  }

  // Propagate the changed value and the new set of selected values to the onChange prop function.
  onChange(changedValue, valuesChecked) {
    this.props.onChange(changedValue, valuesChecked);
  }

  render() {
    return (
      <CheckboxGroup
        {...this.props}
        defaultValuesChecked={undefined}
        valuesChecked={this.props.valuesChecked}
        onChange={this.onChangeCheckedItems.bind(this)}
      >
        {Children.map(this.props.children, child => {
          const Component = child.type;
          const { props: childProps } = child;
          const checked = this.state.childValues.getIn([childProps.value, 'checked']);
          const partiallyChecked = this.state.childValues.getIn([childProps.value, 'partial']);
          const childValues = this.state.childValues.getIn([childProps.value, 'children']);
          const overrideProps = {
            valuesChecked: this.props.valuesChecked,
            checked,
            childValues,
            partiallyChecked,
            nested: true,
          };
          const newProps = Object.assign({}, childProps, overrideProps);
          if (this.props.disabled) {
            newProps.disabled = this.props.disabled;
          }
          return <Component {...newProps} />;
        })}
      </CheckboxGroup>
    );
  }
}

CheckboxParentGroup.propTypes = {
  children: PropTypes.node,
  header: PropTypes.string,
  valuesChecked: PropTypes.array,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  hideHeader: PropTypes.bool,
  hasDivider: PropTypes.bool,
  onChange: PropTypes.func,
  qaId: PropTypes.string,
};

CheckboxParentGroup.defaultProps = {
  valuesChecked: [],
  required: false,
  disabled: false,
  hideHeader: false,
  hasDivider: false,
  onChange: () => {},
};

export default CheckboxParentGroup;



// WEBPACK FOOTER //
// ./src/forms/checkbox-parent-group/index.jsx